import { inject, Service } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { StorageService } from '../modules/checklist/services/storage.service';
import { EnrichedPublishedTemplate } from '../modules/checklist/types/ChecklistTemplate';
import { Checklist } from '../modules/checklist/types/ChecklistTypes';
import {
  ChecklistStatus,
  isTerminalChecklistStatus,
} from '../modules/checklist/types/ChecklistStatus.enum';
import {
  type InitialRepeatableOccurrence,
  startChecklistFromTemplate,
} from '../modules/checklist/utils/checklist-instantiation';
import {
  ChecklistHttpService,
  ChecklistIdMapping,
} from './checklist-http.service';
import { ChecklistLoaderService } from './checklist-loader.service';
import { ChecklistStoreService } from './checklist-store.service';
import { ChecklistWriteSenderService } from './checklist-write-sender.service';
import { DepartmentAccessService } from './department-access.service';
import { NetworkStatusService } from './offline/network-status.service';
import { OfflineStorageService } from './offline/offline-storage.service';
import { TimezoneService } from './timezone.service';

interface PreviewState {
  template: EnrichedPublishedTemplate;
  entryTime: string;
  idMapping: ChecklistIdMapping;
  initialRepeatableOccurrences: InitialRepeatableOccurrence[];
}

export class IsolatedPreviewWriteError extends Error {
  constructor() {
    super('An isolated preview cannot be saved.');
    this.name = 'IsolatedPreviewWriteError';
  }
}

@Service()
export class ChecklistLifecycleService {
  private readonly store = inject(ChecklistStoreService);
  private readonly loader = inject(ChecklistLoaderService);
  private readonly writeSender = inject(ChecklistWriteSenderService);
  private readonly departmentAccess = inject(DepartmentAccessService);
  private readonly storageService = inject(StorageService);
  private readonly checklistHttpService = inject(ChecklistHttpService);
  private readonly networkStatusService = inject(NetworkStatusService);
  private readonly offlineStorageService = inject(OfflineStorageService);
  private readonly timezoneService = inject(TimezoneService);

  private readonly previewStates = new Map<string, PreviewState>();

  private readonly inFlightPromotions = new Map<string, Promise<void>>();

  async updateChecklistStatus(
    checklist: Checklist,
    status: ChecklistStatus
  ): Promise<void> {
    this.departmentAccess.ensureAccess(checklist.department);
    // Completing or discarding ends in a navigation to /home, which an isolated preview does not offer.
    if (this.store.isIsolatedPreview(checklist.id)) {
      return;
    }
    if (!isTerminalChecklistStatus(status) || checklist.status === status) {
      return;
    }

    const timestamp = this.timezoneService.getZonedTimestampNow();

    try {
      await this.writeSender.sendOrQueue(
        checklist.id,
        status === ChecklistStatus.Completed
          ? this.checklistHttpService.completeChecklist
          : this.checklistHttpService.discardChecklist,
        checklist.id,
        timestamp
      );

      const updatedChecklist = {
        ...checklist,
        lastModified: timestamp,
        status,
      };

      this.store.update(updatedChecklist);
      this.storageService.clearChecklistLastSelection(updatedChecklist.id);
    } catch (error) {
      console.error('Error updating checklist status:', error);
      throw error;
    }
  }

  async setSelectedChecklist(id: string | null) {
    if (id === this.store.selectedChecklistId()) return;
    this.store.select(id);
    if (id !== null && !this.store.find(id)) {
      await this.loader.loadChecklist(id);
    }
  }

  startPreviewChecklist(
    template: EnrichedPublishedTemplate,
    entryTime?: string,
    options: { isolated?: boolean } = {}
  ): Checklist {
    this.departmentAccess.ensureAccess(template.departmentName);
    const resolvedEntryTime =
      entryTime ?? this.timezoneService.getZonedTimestampNow();

    const [base, idMapping, initialRepeatableOccurrences] =
      startChecklistFromTemplate(template, resolvedEntryTime);

    const preview: Checklist = {
      ...base,
      phase: 'Preview',
      templateLineageId: template.templateId,
      pinnedPublishedTemplateId: template.id,
    };

    this.previewStates.set(preview.id, {
      template,
      entryTime: resolvedEntryTime,
      idMapping,
      initialRepeatableOccurrences,
    });

    if (options.isolated) {
      this.store.markIsolatedPreview(preview.id);
    }
    this.store.setAll([preview, ...this.store.checklists()], {
      persist: false,
    });
    return preview;
  }

  getPreviewChecklistByLineage(lineageId: string): Checklist | null {
    return (
      this.store
        .checklists()
        .find(
          c =>
            c.phase === 'Preview' &&
            c.templateLineageId === lineageId &&
            !this.store.isIsolatedPreview(c.id)
        ) ?? null
    );
  }

  cancelPreviewChecklist(id: string): void {
    this.previewStates.delete(id);
    this.store.unmarkIsolatedPreview(id);
    this.storageService.clearChecklistLastSelection(id);
    this.store.setAll(
      this.store.checklists().filter(c => c.id !== id),
      { persist: false }
    );
    if (this.store.selectedChecklistId() === id) {
      this.store.select(null);
    }
  }

  async promotePreviewIfNeeded(id: string): Promise<void> {
    const existing = this.inFlightPromotions.get(id);
    if (existing) {
      await existing;
      return;
    }
    const local = this.store.find(id);
    if (!local || local.phase !== 'Preview') {
      return;
    }
    if (this.store.isIsolatedPreview(id)) {
      throw new IsolatedPreviewWriteError();
    }
    const state = this.previewStates.get(id);
    if (!state) {
      // A Preview without companion state cannot promote. Flip to Started so it is not retried.
      this.store.setAll(
        this.store
          .checklists()
          .map(c => (c.id === id ? { ...c, phase: 'Started' } : c)),
        { persist: false }
      );
      return;
    }

    const promotion = this.runPromotion(id, local, state);
    this.inFlightPromotions.set(id, promotion);
    try {
      await promotion;
    } finally {
      this.inFlightPromotions.delete(id);
    }
  }

  private async runPromotion(
    id: string,
    local: Checklist,
    state: PreviewState
  ): Promise<void> {
    await this.sendStart(
      state.template.id,
      state.entryTime,
      state.idMapping,
      state.initialRepeatableOccurrences
    );

    // Keep the local Preview structure and only flip the phase because replacing it with the API response
    // resets in-progress form values and re-fires change events into the write chokepoint.
    const promoted: Checklist = { ...local, phase: 'Started' };
    this.store.replaceWithoutBump(promoted);
    this.previewStates.delete(id);
  }

  private async sendStart(
    templateId: string,
    entryTime: string,
    idMapping: ChecklistIdMapping,
    initialRepeatableOccurrences: InitialRepeatableOccurrence[]
  ): Promise<void> {
    if (!this.networkStatusService.online()) {
      await this.offlineStorageService.addAction(
        this.checklistHttpService.startChecklist,
        templateId,
        entryTime,
        idMapping
      );
      await this.queueInitialRepeatableOccurrences(
        idMapping.checklistId,
        initialRepeatableOccurrences
      );
      return;
    }

    await firstValueFrom(
      this.checklistHttpService.startChecklist(templateId, entryTime, idMapping)
    );
    await this.persistInitialRepeatableOccurrences(
      idMapping.checklistId,
      initialRepeatableOccurrences
    );
  }

  private async persistInitialRepeatableOccurrences(
    checklistId: string,
    occurrences: InitialRepeatableOccurrence[]
  ): Promise<void> {
    for (const occurrence of occurrences) {
      try {
        await firstValueFrom(
          this.checklistHttpService.addRepeatableChecklistItem(
            checklistId,
            occurrence.sectionId,
            occurrence.prototypeId,
            occurrence.idTree
          )
        );
      } catch (error) {
        if (this.writeSender.isNonQueueableHttpFailure(error)) {
          throw error;
        }
        await this.queueInitialRepeatableOccurrences(checklistId, [occurrence]);
      }
    }
  }

  private async queueInitialRepeatableOccurrences(
    checklistId: string,
    occurrences: InitialRepeatableOccurrence[]
  ): Promise<void> {
    for (const occurrence of occurrences) {
      await this.offlineStorageService.addAction(
        this.checklistHttpService.addRepeatableChecklistItem,
        checklistId,
        occurrence.sectionId,
        occurrence.prototypeId,
        occurrence.idTree
      );
    }
  }
}
